import React, { useState } from 'react';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, CalendarOff, X } from 'lucide-react';

const TYPES = [
  { key: 'pto', label: 'PTO / Vacation' },
  { key: 'sick', label: 'Sick' },
  { key: 'unpaid', label: 'Unpaid' },
  { key: 'other', label: 'Other' },
];

export const TimeOffModal = ({ staff, onClose, reload }) => {
  const [form, setForm] = useState({ user_id: '', type: 'pto', start_date: '', end_date: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v, ...(k === 'start_date' && !f.end_date ? { end_date: v } : {}) }));

  const submit = async () => {
    if (!form.user_id || !form.start_date || !form.end_date) { toast.error('Pick a staff member and dates'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/time-off', form);
      toast.success(`Time off entered for ${data.user_name} — auto-approved. Optimizer will avoid those days.`);
      onClose(); reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save time off');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#17141F] border border-white/15 rounded-2xl p-5 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()} data-testid="timeoff-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2"><CalendarOff className="h-4 w-4 text-[#FFD66B]" /> Enter staff time off</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="timeoff-close"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-xs text-white/40">Entered on their behalf — auto-approved instantly. Suggestions, AI models, and conflict radar will respect it.</p>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-white/40">Staff member</label>
          <select value={form.user_id} onChange={e => set('user_id', e.target.value)}
                  className="w-full h-11 px-3 rounded-xl bg-white/5 border border-white/15 text-sm font-semibold" data-testid="timeoff-staff">
            <option value="" className="text-black">Select staff…</option>
            {staff.map(s => <option key={s.id} value={s.id} className="text-black">{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-white/40">Type</label>
          <div className="flex gap-1.5 flex-wrap">
            {TYPES.map(t => (
              <button key={t.key} onClick={() => set('type', t.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border ${form.type === t.key ? 'bg-[#FFD66B] text-[#1A1325] border-[#FFD66B]' : 'text-white/60 border-white/15'}`}
                      data-testid={`timeoff-type-${t.key}`}>{t.label}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-white/40">From</label>
            <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)}
                   className="w-full h-11 px-3 rounded-xl bg-white/5 border border-white/15 text-sm" data-testid="timeoff-start" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-white/40">To</label>
            <input type="date" value={form.end_date} min={form.start_date} onChange={e => set('end_date', e.target.value)}
                   className="w-full h-11 px-3 rounded-xl bg-white/5 border border-white/15 text-sm" data-testid="timeoff-end" />
          </div>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-white/40">Reason (optional)</label>
          <input value={form.reason} onChange={e => set('reason', e.target.value)} placeholder="e.g. family vacation"
                 className="w-full h-11 px-3 rounded-xl bg-white/5 border border-white/15 text-sm" data-testid="timeoff-reason" />
        </div>
        <button onClick={submit} disabled={saving}
                className="w-full h-11 rounded-xl bg-[#FFD66B] text-[#1A1325] font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="timeoff-submit">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save time off (auto-approved)
        </button>
      </div>
    </div>
  );
};

export default TimeOffModal;
